import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../common/context/AuthContext';
import PermissionDenied from '../../../common/components/PermissionDenied';
import { ArrowLeft, CheckCircle2, Shield } from 'lucide-react';
import { toast } from 'react-toastify';
import { API_URL } from '../../../api/config';

const ChangeManagerPin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userToken: token, userRole } = useAuth();
  
  const managerModules = location.state?.modules || {};
  
  const [step, setStep] = useState(1);
  const [currentPin, setCurrentPin] = useState(['', '', '', '']);
  const [newPin, setNewPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(location.state?.phoneNumber || '');
  
  const currentPinRefs = useRef([]);
  const newPinRefs = useRef([]);
  const confirmPinRefs = useRef([]);

  useEffect(() => {
    if (userRole !== 'admin') {
      toast.error('Only admins can change manager PIN');
      navigate('/pin-protection');
    }
  }, [userRole, navigate]);

  // Cleanup session on unmount
  useEffect(() => {
    return () => {
      if (sessionToken) {
        clearSession();
      }
    };
  }, [sessionToken]);

  const clearSession = async () => {
    try {
      await fetch(`${API_URL}/pin-settings/clear-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: 'admin' }) // Using admin role for verification
      });
    } catch (error) {
      console.error('Error clearing session:', error);
    }
  };

  const handleVerifyCurrentPin = async () => {
    const pinString = currentPin.join('');
    
    if (pinString.length !== 4) {
      toast.error('Please enter complete current PIN');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/pin-settings/verify-for-change`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          currentPin: pinString, 
          role: 'admin' // Admin must verify their own PIN to change manager PIN
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSessionToken(data.sessionToken);
        toast.success('PIN verified. Now set new manager PIN.');
        setStep(2);
        setCurrentPin(['', '', '', '']);
      } else {
        toast.error(data.message || 'Invalid PIN');
        setCurrentPin(['', '', '', '']);
        currentPinRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error('Error verifying PIN:', error);
      toast.error('Failed to verify PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePin = async () => {
    const newPinString = newPin.join('');
    const confirmPinString = confirmPin.join('');

    if (newPinString.length !== 4) {
      toast.error('Please enter 4-digit new PIN');
      return;
    }

    if (step === 2) {
      setStep(3);
      return;
    }

    if (newPinString !== confirmPinString) {
      toast.error('PINs do not match');
      setConfirmPin(['', '', '', '']);
      confirmPinRefs.current[0]?.focus();
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/pin-settings/change-manager-pin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          currentPin: currentPin.join(''),
          newPin: newPinString,
          phoneNumber,
          modules: managerModules,
          sessionToken 
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Manager PIN changed successfully');
        setSessionToken(null);
        setTimeout(() => navigate('/pin-protection'), 1500);
      } else {
        toast.error(data.message || 'Failed to change PIN');
      }
    } catch (error) {
      console.error('Error changing PIN:', error);
      toast.error('Failed to change PIN');
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (index, value, setState, refs, step = 'current') => {
    if (value.length > 1) return;
    if (value && !/^\d+$/.test(value)) return;

    const newState = [...(step === 'current' ? currentPin : step === 'new' ? newPin : confirmPin)];
    newState[index] = value;
    
    if (step === 'current') setCurrentPin(newState);
    else if (step === 'new') setNewPin(newState);
    else setConfirmPin(newState);

    if (value && index < 3) {
      refs.current[index + 1]?.focus();
    }
  };

  const handlePinKeyDown = (index, e, refs, pinArray) => {
    if (e.key === 'Backspace' && !pinArray[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const enabledModulesCount = Object.values(managerModules).filter(Boolean).length;

  if (userRole !== 'admin') {
    return (
      <PermissionDenied
        variant="fullPage"
        title="We are Sorry..."
        message="Only administrators can change manager PIN settings. Please contact your system administrator."
        onGoBack={() => navigate('/pin-protection')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <button
          onClick={() => {
            if (sessionToken) clearSession();
            navigate(-1);
          }}
          className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-8 text-white">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4">
              <Shield size={32} />
            </div>
            <h1 className="text-2xl font-bold mb-2">Change Manager PIN</h1>
            <p className="text-blue-100 text-sm font-medium">
              {step === 1 && "Verify your admin PIN to change manager PIN"}
              {step === 2 && "Create a new 4-digit PIN"}
              {step === 3 && "Confirm the new PIN"}
            </p>
          </div>

          {/* Current Modules Info */}
          {enabledModulesCount > 0 && (
            <div className="px-8 py-4 bg-blue-50 border-b border-blue-100">
              <p className="text-xs font-bold text-blue-900 mb-2">
                Current Manager Access
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(managerModules)
                  .filter(([_, enabled]) => enabled)
                  .map(([module]) => (
                    <span key={module} className="px-2 py-1 bg-white text-blue-700 text-[10px] font-bold rounded-md border border-blue-200">
                      {module.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  ))}
              </div>
            </div>
          )}

          {/* Progress Steps */}
          <div className="px-8 py-6 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((stepNum) => (
                <React.Fragment key={stepNum}>
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                      step >= stepNum 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-slate-200 text-slate-400'
                    }`}>
                      {step > stepNum ? (
                        <CheckCircle2 size={20} />
                      ) : (
                        stepNum
                      )}
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 mt-2 uppercase tracking-wider">
                      {stepNum === 1 && 'Verify'}
                      {stepNum === 2 && 'New PIN'}
                      {stepNum === 3 && 'Confirm'}
                    </span>
                  </div>
                  {stepNum < 3 && (
                    <div className={`flex-1 h-0.5 mx-2 transition-all ${
                      step > stepNum ? 'bg-blue-600' : 'bg-slate-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Step 1: Verify Admin PIN */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Enter Your Admin PIN
                  </label>
                  <div className="flex gap-3 justify-center">
                    {currentPin.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (currentPinRefs.current[index] = el)}
                        type="password"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handlePinChange(index, e.target.value, setCurrentPin, currentPinRefs, 'current')}
                        onKeyDown={(e) => handlePinKeyDown(index, e, currentPinRefs, currentPin)}
                        className="w-14 h-16 text-center text-2xl font-bold border-2 border-slate-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-center text-slate-500 font-medium">
                    Verify your admin PIN to change manager PIN
                  </p>
                </div>
                <button
                  onClick={handleVerifyCurrentPin}
                  disabled={loading || currentPin.join('').length !== 4}
                  className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all transform active:scale-95 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Verify PIN'}
                </button>
              </div>
            )}

            {/* Step 2: Enter New PIN */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Create New 4-Digit PIN
                  </label>
                  <div className="flex gap-3 justify-center">
                    {newPin.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (newPinRefs.current[index] = el)}
                        type="password"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handlePinChange(index, e.target.value, setNewPin, newPinRefs, 'new')}
                        onKeyDown={(e) => handlePinKeyDown(index, e, newPinRefs, newPin)}
                        className="w-14 h-16 text-center text-2xl font-bold border-2 border-slate-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-center text-slate-500 font-medium">
                    This is the PIN manager will use for access
                  </p>
                </div>
                <button
                  onClick={handleChangePin}
                  disabled={newPin.join('').length !== 4}
                  className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all transform active:scale-95 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            )}

            {/* Step 3: Confirm PIN */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Confirm New PIN
                  </label>
                  <div className="flex gap-3 justify-center">
                    {confirmPin.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (confirmPinRefs.current[index] = el)}
                        type="password"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handlePinChange(index, e.target.value, setConfirmPin, confirmPinRefs, 'confirm')}
                        onKeyDown={(e) => handlePinKeyDown(index, e, confirmPinRefs, confirmPin)}
                        className="w-14 h-16 text-center text-2xl font-bold border-2 border-slate-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-center text-slate-500 font-medium">
                    Re-enter to confirm
                  </p>
                </div>
                <button
                  onClick={handleChangePin}
                  disabled={loading || confirmPin.join('').length !== 4}
                  className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all transform active:scale-95 disabled:cursor-not-allowed"
                >
                  {loading ? 'Changing PIN...' : 'Change Manager PIN'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-xs text-blue-900 font-medium leading-relaxed">
            🔒 Your session is secure and will expire after 5 minutes of inactivity. The manager will need their phone number to receive OTP.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChangeManagerPin;